import { useState, useEffect, useCallback } from "react";
import {
  generateAndUploadThumbnailEvidence,
  getMediaType,
  getUrlExtension,
  isValidMimeType
} from "../../utils/custom-utils";
import { guessContentType, proxiedUrlFor } from "../../utils/media-url-utils";
import configs from "../../utils/configs";
import { useUploadForm } from "../contexts/UploadFormContext";
import { getAuthHeaders } from "../../utils/getToken";
import { useWorkspaceContext } from "../contexts/WorkspaceContext";
import { uploadClaimFields } from "../../utils/upload-claim";
import { acquireWorkspaceMediaForSpawn } from "../../utils/workspace-media-acquisition-utils";
import { requireWorkspaceUploadId } from "../../utils/workspace-scope-utils";

const possibleLinkExtensions = ["gif", "png", "jpg", "jpeg", "webp", "mp4", "mp3", "pdf", "glb"];

const folder = `assets360/${configs.APP_NAME}/my-media`;

const useUrlUpload = () => {
  const { onErrorUrlUploadChange, errorUploadUrl } = useUploadForm();
  const { selectedWorkspace } = useWorkspaceContext();
  const [mimeType, setMimeType] = useState(null);
  const urlUpload = useCallback(
    async (url, workspaceOverride = null) => {
      if (!url) {
        return;
      }

      const targetWorkspace = workspaceOverride || selectedWorkspace;
      const workspaceId = requireWorkspaceUploadId(targetWorkspace?.id);

      const mimeType = await fetchMimeType(url);
      if (!mimeType) {
        // Preserve page/embed URL acquisition while collecting Asset metadata in the drawer.
        const acquired = await acquireWorkspaceMediaForSpawn(url, { workspaceId });
        if (acquired?.asset?.id) {
          onErrorUrlUploadChange(null);
          return acquired.asset;
        }
        return null;
      }

      const name = url.split("/").pop();
      // Build assets URL with workspace
      const baseUrl = configs["PERSISTENCE_API_URL"];

      const myMediaUrl = `${baseUrl}/workspaces/${workspaceId}/assets`;

      try {
        const thumbnailEvidence =
          mimeType.includes("image") ||
          mimeType.includes("video") ||
          mimeType.includes("pdf") ||
          mimeType.includes("model") ||
          mimeType == "application/octet-stream"
            ? await generateAndUploadThumbnailEvidence({ url, folder, name, mimeType, workspaceId })
            : null;
        const thumbnail = thumbnailEvidence?.url ?? null;

        const entry = {
          name: name,
          title: name,
          path: url,
          type: getMediaType(url),
          asset_type: "my-media",
          thumbnail: thumbnail,
          public_thumbnail: thumbnail,
          source: "metaverse",
          ...uploadClaimFields(null, thumbnailEvidence?.signedUpload)
        };

        if (thumbnail) {
          entry.thumbnail = thumbnail;
        }

        // send to my media
        const myMediaResponse = await fetch(myMediaUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
            ...(await getAuthHeaders(myMediaUrl, "POST"))
          },
          body: JSON.stringify(entry)
        });

        if (myMediaResponse.status === 403) {
          const error = new Error("Upload forbidden");
          error.status = 403;
          throw error;
        }

        if (!myMediaResponse.ok) {
          const error = new Error("Error uploading URL");
          error.status = myMediaResponse.status;
          throw error;
        }

        const myMediaJson = await myMediaResponse.json();
        if (myMediaJson?.error) {
          onErrorUrlUploadChange("Error uploading URL");
          return null;
        }

        return myMediaJson?.data;
      } catch (error) {
        if (error?.status === 403) {
          throw error;
        }
        console.error("Error uploading URL:", error);
        onErrorUrlUploadChange("Error uploading URL");
        return null;
      }
    },
    [selectedWorkspace, onErrorUrlUploadChange]
  );

  async function fetchMimeType(url) {
    try {
      // Fetch only the headers of the URL
      const proxiedUrl = proxiedUrlFor(url);

      const response = await fetch(proxiedUrl, { method: "HEAD" });
      const contentType = response.headers.get("Content-Type");
      // Display the MIME type or a fallback message
      // setMimeType(contentType || 'Unknown MIME type');
      if (!contentType) {
        onErrorUrlUploadChange("Unknown MIME type");
      }

      if (isValidMimeType(contentType)) {
        return contentType;
      }
      onErrorUrlUploadChange("File type is not valid");

      return null;
    } catch (error) {
      console.error("Error fetching MIME type:", error);
      onErrorUrlUploadChange("Error fetching MIME type");
      return null;
    }
  }

  return {
    urlUpload,
    errorUpload: errorUploadUrl,
    onErrorUrlUploadChange
  };
};

export default useUrlUpload;
